import type { Chart } from "./Chart.js";

export interface ChartLinkOptions {
  /** Zoom and pan together. On by default. */
  zoom?: boolean;
  /** Hover together: picking out a period in one picks it out in all. On by default. */
  hover?: boolean;
  /**
   * Line the plots up: every chart keeps as much room beside its plot as the widest-labeled of
   * them needs, so a price chart and the RSI under it start and end at the same x. On by default.
   * Only charts standing up are aligned, and only the visible ones count.
   */
  axes?: boolean;
}

/** The plot's insets from the chart's own content area, left and right. */
export interface ChartInsets {
  left: number;
  right: number;
}

const links = new WeakMap<Chart, ChartLink>();

/** The link a chart belongs to, or null. Internal: `Chart` asks it while laying out. */
export function linkOf(chart: Chart): ChartLink | null {
  return links.get(chart) ?? null;
}

/**
 * Charts that move as one - a price chart with its volume and its RSI stacked under it.
 *
 * Zooming or panning any of them zooms and pans the rest; hovering a period picks out the same
 * period in all of them (matched by x on a numeric or time axis, so they need not hold the same
 * number of values, and by index otherwise); and the room beside each plot is made the same, so
 * the plots line up without anyone measuring a label. The charts are expected to share a category
 * or x axis: zoom is passed across as a fraction of the axis.
 *
 * A chart belongs to one link at a time; adding it to another takes it out of the first.
 *
 * ```ts
 * const link = new ChartLink([price, volume, strength]);
 * link.remove(volume);
 * link.dispose();
 * ```
 */
export class ChartLink {
  readonly zoom: boolean;
  readonly hover: boolean;
  readonly axes: boolean;

  #charts: Chart[] = [];
  #handlers = new Map<Chart, { zoom: () => void; hover: () => void; shown: () => void }>();
  #insets = new Map<Chart, ChartInsets>();
  #shared: ChartInsets = { left: 0, right: 0 };
  /** Set while passing a change across, so the echo from the other charts is ignored. */
  #syncing = false;

  constructor(charts: readonly Chart[] = [], options: ChartLinkOptions = {}) {
    this.zoom = options.zoom ?? true;
    this.hover = options.hover ?? true;
    this.axes = options.axes ?? true;
    for (const chart of charts) this.add(chart);
  }

  get charts(): readonly Chart[] {
    return this.#charts;
  }

  /** Link `chart` in, taking the view the others already have. */
  add(chart: Chart): void {
    if (this.#charts.includes(chart)) return;
    links.get(chart)?.remove(chart);
    const first = this.#charts[0];
    this.#charts.push(chart);
    links.set(chart, this);
    const handlers = {
      zoom: () => this.#zoomFrom(chart),
      hover: () => this.#hoverFrom(chart),
      // A chart shown or hidden joins or leaves the alignment.
      shown: () => this.#realign(),
    };
    this.#handlers.set(chart, handlers);
    chart.on("zoom", handlers.zoom);
    chart.on("selectionchanged", handlers.hover);
    chart.on("visiblechanged", handlers.shown);
    if (first && this.zoom) this.#quietly(() => chart.zoomTo(first.view.from, first.view.to));
    this.#realign();
  }

  remove(chart: Chart): void {
    const i = this.#charts.indexOf(chart);
    if (i < 0) return;
    this.#charts.splice(i, 1);
    const handlers = this.#handlers.get(chart);
    if (handlers) {
      chart.off("zoom", handlers.zoom);
      chart.off("selectionchanged", handlers.hover);
      chart.off("visiblechanged", handlers.shown);
    }
    this.#handlers.delete(chart);
    this.#insets.delete(chart);
    links.delete(chart);
    chart.invalidate();
    this.#realign();
  }

  /** Unlink every chart. */
  dispose(): void {
    for (const chart of [...this.#charts]) this.remove(chart);
  }

  /**
   * Record the room `chart` needs beside its plot and answer the room it should take: the most any
   * visible linked chart needs. Called by `Chart` as it lays out. When the shared room changes,
   * the other charts are asked to paint again, and settle on the next frame.
   */
  alignInsets(chart: Chart, needs: ChartInsets): ChartInsets {
    if (!this.axes) return needs;
    const before = this.#insets.get(chart);
    if (!before || before.left !== needs.left || before.right !== needs.right) {
      this.#insets.set(chart, { ...needs });
      this.#realign(chart);
    }
    return {
      left: Math.max(needs.left, this.#shared.left),
      right: Math.max(needs.right, this.#shared.right),
    };
  }

  /** Work out the shared room again, and repaint the charts it changed for (all but `except`). */
  #realign(except?: Chart): void {
    let left = 0;
    let right = 0;
    for (const chart of this.#charts) {
      const insets = this.#insets.get(chart);
      if (!insets || !chart.effectivelyVisible || chart.radial || chart.orientation !== "vertical") continue;
      left = Math.max(left, insets.left);
      right = Math.max(right, insets.right);
    }
    if (left === this.#shared.left && right === this.#shared.right) return;
    this.#shared = { left, right };
    for (const chart of this.#charts) if (chart !== except) chart.invalidate();
  }

  #quietly(fn: () => void): void {
    if (this.#syncing) return;
    this.#syncing = true;
    try {
      fn();
    } finally {
      this.#syncing = false;
    }
  }

  #zoomFrom(source: Chart): void {
    if (!this.zoom) return;
    const { from, to } = source.view;
    this.#quietly(() => {
      for (const chart of this.#charts) if (chart !== source) chart.zoomTo(from, to);
    });
  }

  #hoverFrom(source: Chart): void {
    if (!this.hover) return;
    const index = source.hoveredIndex;
    // A single scatter point has no period to share.
    const shared = index >= 0 && source.hoveredSeries < 0;
    this.#quietly(() => {
      for (const chart of this.#charts) {
        if (chart === source) continue;
        chart.hoveredIndex = shared ? matchIndex(source, chart, index) : -1;
      }
    });
  }
}

/**
 * The index in `target` of value `index` of `source`: the one at the same x when both have one,
 * else the same index. A target whose x values rise is searched by halves; anything else is
 * scanned. No match at all is -1, so a chart with no such period shows nothing rather than the
 * wrong one.
 */
export function matchIndex(source: Chart, target: Chart, index: number): number {
  const x = source.xScale === "category" ? null : source.xOf(0, index);
  if (x === null || target.xScale === "category") return index < target.categoryCount ? index : -1;
  const count = target.categoryCount;
  let lo = 0;
  let hi = count - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = target.xOf(0, mid);
    if (v === null) break;
    if (v === x) return mid;
    if (v < x) lo = mid + 1;
    else hi = mid - 1;
  }
  for (let i = 0; i < count; i++) if (target.xOf(0, i) === x) return i;
  return -1;
}
